import { P } from "./bookrPaths";

/**
 * Bookr logo (design system v2), the real brand artwork, as vector.
 *
 * The mark is a two-tone B: an ink upper bowl and a teal lower bowl cradling
 * an avatar head and a checkmark, person + confirmation, which is exactly
 * what Bookr does. Path data comes from the designer's vector (see
 * `bookrPaths.ts`); only the fills are ours.
 *
 * Colors are tokens, not fixed hex. The ink parts use `--c-ink` so they invert
 * to near-white in dark mode instead of disappearing on a dark surface, and the
 * teal parts use `--c-teal`, which brightens for dark backgrounds. In light
 * mode both resolve to the exact brand values (#06183D / #1CB9A8). Pass
 * `tone="brand"` to force those literals on a surface you know is light.
 *
 * `animate` runs the assembly once on mount: each bowl draws its own outline
 * (via `pathLength={1}`, so no path measuring is needed), the fills wash in,
 * the head drops, then the remaining letters stagger in. Pure CSS, no client
 * component and no JS. It all collapses under prefers-reduced-motion via the
 * global rule in globals.css.
 *
 * Decorative: callers that render it without visible text should pass an
 * aria-label via the wrapping link.
 */

const MARK_VIEWBOX = "109.5 34.5 559 710";
const LOCKUP_VIEWBOX = "109.5 34.5 1857 710";
const MARK_RATIO = 559 / 710;
const LOCKUP_RATIO = 1857 / 710;

function useTone(tone: "auto" | "brand") {
  return tone === "brand"
    ? { ink: "#06183D", teal: "#1CB9A8" }
    : { ink: "var(--c-ink)", teal: "var(--c-teal)" };
}

/** The B monogram on its own, favicons, avatars, tight chrome, loading states. */
export function BookrMark({
  size = 34,
  animate = false,
  tone = "auto",
  className,
  title = "Bookr",
}: {
  size?: number;
  animate?: boolean;
  tone?: "auto" | "brand";
  className?: string;
  title?: string;
}) {
  const { ink, teal } = useTone(tone);

  return (
    <svg
      width={size * MARK_RATIO}
      height={size}
      viewBox={MARK_VIEWBOX}
      role="img"
      aria-label={title}
      className={`bookr-mark${animate ? " is-animated" : ""}${
        className ? ` ${className}` : ""
      }`}
    >
      <MarkPaths ink={ink} teal={teal} />
    </svg>
  );
}

/** The full "Bookr" lockup, headers, auth screens, footers, the landing hero. */
export function BookrWordmark({
  size = 30,
  animate = false,
  tone = "auto",
  className,
  title = "Bookr",
}: {
  size?: number;
  animate?: boolean;
  tone?: "auto" | "brand";
  className?: string;
  title?: string;
}) {
  const { ink, teal } = useTone(tone);

  return (
    <svg
      width={size * LOCKUP_RATIO}
      height={size}
      viewBox={LOCKUP_VIEWBOX}
      role="img"
      aria-label={title}
      className={`bookr-mark${animate ? " is-animated" : ""}${
        className ? ` ${className}` : ""
      }`}
    >
      <MarkPaths ink={ink} teal={teal} />
      <path className="bm-l1" d={P.O1} fill={ink} fillRule="evenodd" />
      <path className="bm-l2" d={P.O2} fill={ink} fillRule="evenodd" />
      <path className="bm-l3" d={P.K} fill={ink} />
      <path className="bm-l4" d={P.R} fill={teal} />
    </svg>
  );
}

/**
 * The B itself. Each bowl is stroked in its own fill color and normalized to
 * `pathLength={1}`, so the draw-on animation is a dash offset from 1 → 0 with
 * no need to measure real path lengths. Once filled, the stroke is invisible
 * (same color), so the resting state is pixel-identical to the artwork.
 */
function MarkPaths({ ink, teal }: { ink: string; teal: string }) {
  return (
    <>
      <path
        className="bm-top"
        d={P.B_INK}
        fill={ink}
        stroke={ink}
        strokeWidth={4}
        pathLength={1}
      />
      <path
        className="bm-bottom"
        d={P.B_TEAL}
        fill={teal}
        stroke={teal}
        strokeWidth={4}
        pathLength={1}
      />
      <path className="bm-head" d={P.B_HEAD} fill={teal} />
    </>
  );
}

/**
 * The logo as used across app chrome. A thin wrapper over BookrWordmark so
 * chrome has a single thing to call and spacing stays consistent.
 */
export function BookrLogo({
  size = 30,
  animate = false,
  tone = "auto",
  className,
}: {
  size?: number;
  animate?: boolean;
  tone?: "auto" | "brand";
  className?: string;
}) {
  return (
    <span className={`inline-flex items-center ${className ?? ""}`}>
      <BookrWordmark size={size} animate={animate} tone={tone} />
    </span>
  );
}
